import { logger } from '../logger';
import { getHotelId } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';
import { supabaseClient } from '../supabase';

let analyticsServiceInstance: any = null;

class SessionTracker {
  private static instance: SessionTracker;
  private sessionStartTime: number;
  private lastActivityTime: number;
  private pageViews: number = 0;
  private interactions: number = 0;
  private isTracking: boolean = false;
  private inactivityTimeout: number = 30 * 60 * 1000;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private anonymousId: string;
  private language: string | null = null;
  private ipAddress: string | null = null;
  private previousLanguage: string | null = null;
  private hasSessionEnded: boolean = false;
  private hotelId: string;
  private hotelSlug: string | null = null;

  private constructor() {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.sessionId = this.generateSessionId();
    this.anonymousId = this.getOrCreateAnonymousId();
    this.hotelId = getHotelId();
    this.setupEventListeners();
    this.captureIpAddress();
  }

  public static getInstance(): SessionTracker {
    if (!SessionTracker.instance) {
      SessionTracker.instance = new SessionTracker();
    }
    return SessionTracker.instance;
  }

  public setAnalyticsService(service: any): void {
    analyticsServiceInstance = service;
  }

  public setHotelInfo(hotelId: string, hotelSlug: string | null): void {
    this.hotelId = hotelId;
    this.hotelSlug = hotelSlug;
    
    logger.debug('Session tracker hotel info updated', {
      category: 'Analytics',
      data: { 
        hotelId,
        hotelSlug,
        sessionId: this.sessionId
      }
    });
  }

  private generateSessionId(): string {
    return uuidv4();
  }

  private getOrCreateAnonymousId(): string {
    try {
      const storedId = localStorage.getItem('anonymous_id');
      if (storedId) return storedId;
      const newId = uuidv4();
      localStorage.setItem('anonymous_id', newId);
      return newId;
    } catch {
      return uuidv4();
    }
  }

  private async captureIpAddress(): Promise<void> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) throw new Error(`Failed to fetch IP: ${response.status}`);
      const data = await response.json();
      this.ipAddress = data.ip;
      logger.debug('Captured IP address', {
        category: 'Analytics',
        data: { ipAddress: this.ipAddress }
      });
    } catch (error) {
      logger.error('Failed to capture IP address', {
        category: 'Analytics',
        data: { error }
      });
      this.ipAddress = null;
    }
  }

  private setupEventListeners(): void {
    ['click', 'touchstart', 'mousemove', 'keypress', 'scroll'].forEach(eventType => {
      window.addEventListener(eventType, this.handleUserActivity.bind(this), { passive: true });
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }

  private handleUserActivity(): void {
    this.lastActivityTime = Date.now();
    this.interactions++;
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => this.trackInactivity(), this.inactivityTimeout);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      if (!this.hasSessionEnded) this.trackSessionEnd();
    } else {
      this.trackSessionResume();
    }
  }

  private handleBeforeUnload(): void {
    if (!this.hasSessionEnded) {
      logger.debug('Session ending, no session_end update to sessions table', {
        sessionId: this.sessionId
      });
    }
  }

  private async trackInactivity(): Promise<void> {
    if (!analyticsServiceInstance) return;
    const inactiveTime = (Date.now() - this.lastActivityTime) / 1000;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'user_inactivity',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        inactive_time: inactiveTime,
        last_active_path: window.location.pathname,
        timestamp: new Date().toISOString()
      }
    });
  }

  private async trackSessionPause(): Promise<void> {
    if (!analyticsServiceInstance) return;
    const sessionDuration = (Date.now() - this.sessionStartTime) / 1000;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_pause',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        duration: sessionDuration,
        page_views: this.pageViews,
        interactions: this.interactions,
        language: this.language,
        timestamp: new Date().toISOString()
      }
    });
  }

  private async trackSessionResume(): Promise<void> {
    if (!analyticsServiceInstance) return;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_resume',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        pause_duration: (Date.now() - this.lastActivityTime) / 1000,
        timestamp: new Date().toISOString()
      }
    });
    this.lastActivityTime = Date.now();
  }

  private async trackSessionEnd(): Promise<void> {
    if (this.hasSessionEnded || !analyticsServiceInstance) return;
    this.hasSessionEnded = true;
    const sessionDuration = (Date.now() - this.sessionStartTime) / 1000;
    analyticsServiceInstance.trackEvent({
      event_type: 'session',
      event_name: 'session_end',
      event_data: {
        hotel_id: this.hotelId,
        hotel_slug: this.hotelSlug,
        session_id: this.sessionId,
        anonymous_id: this.anonymousId,
        duration: sessionDuration,
        page_views: this.pageViews,
        interactions: this.interactions,
        language: this.language,
        timestamp: new Date().toISOString()
      }
    });
  }

  public incrementPageView(): void {
    this.pageViews++;
  }

  public incrementInteraction(): void {
    this.interactions++;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getAnonymousId(): string {
    return this.anonymousId;
  }

  public async updateLanguage(language: string): Promise<void> {
    if (!language || language === this.language) return;
    this.previousLanguage = this.language;
    this.language = language;
    if (analyticsServiceInstance) {
      analyticsServiceInstance.trackEvent({
        event_type: 'user',
        event_name: 'language_change',
        event_data: {
          from_language: this.previousLanguage,
          to_language: language,
          hotel_id: this.hotelId,
          hotel_slug: this.hotelSlug,
          timestamp: new Date().toISOString()
        }
      });
    }
    try {
      const { error } = await supabaseClient
        .from('sessions')
        .update({ language: language, updated_at: new Date().toISOString() })
        .eq('id', this.sessionId);
      if (error) {
        logger.error('Error updating session language', {
          category: 'Analytics',
          data: { error, sessionId: this.sessionId, language }
        });
      }
    } catch (error) {
      logger.error('Error updating session language', {
        category: 'Analytics',
        data: { error, sessionId: this.sessionId, language }
      });
    }
  }

  public async startTracking(): Promise<void> {
    if (this.isTracking) return;
    this.isTracking = true;
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.pageViews = 0;
    this.interactions = 0;
    this.hasSessionEnded = false;

    // Get hotel info from window.HOTEL_CONFIG if available
    if (window.HOTEL_CONFIG?.id) {
      this.hotelId = window.HOTEL_CONFIG.id;
      this.hotelSlug = window.HOTEL_CONFIG.slug;
    }

    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng) {
      this.language = i18nextLng.split('-')[0].toLowerCase();
    }

    if (!this.ipAddress) {
      await this.captureIpAddress();
    }

    try {
      await supabaseClient
        .from('sessions')
        .insert({
          id: this.sessionId,
          hotel_id: this.hotelId,
          anonymous_id: this.anonymousId,
          start_time: new Date().toISOString(),
          referrer: document.referrer || 'direct',
          is_returning_user: localStorage.getItem('has_visited') === 'true',
          device_type: this.getDeviceType(),
          browser: this.getBrowser(),
          os: this.getOS(),
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          is_pwa: window.matchMedia('(display-mode: standalone)').matches,
          language: this.language,
          ip_address: this.ipAddress,
          is_active: true
        });
    } catch (error) {
      logger.error('Error creating session record', {
        category: 'Analytics',
        data: { error, sessionId: this.sessionId }
      });
    }

    if (analyticsServiceInstance) {
      analyticsServiceInstance.trackEvent({
        event_type: 'session',
        event_name: 'session_start',
        event_data: {
          hotel_id: this.hotelId,
          hotel_slug: this.hotelSlug,
          session_id: this.sessionId,
          anonymous_id: this.anonymousId,
          referrer: document.referrer || 'direct',
          is_returning_user: localStorage.getItem('has_visited') === 'true',
          device_type: this.getDeviceType(),
          browser: this.getBrowser(),
          os: this.getOS(),
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          is_pwa: window.matchMedia('(display-mode: standalone)').matches,
          language: this.language,
          ip_address: this.ipAddress,
          timestamp: new Date().toISOString()
        }
      });
    }

    this.inactivityTimer = setTimeout(() => {
      this.trackInactivity();
    }, this.inactivityTimeout);
  }

  private getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Mac OS X/.test(ua)) return 'MacOS';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Unknown';
  }
}

export const sessionTracker = SessionTracker.getInstance();